import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { AuthResponse } from '../interfaces/auth.interface';
import { RegisterRequest } from '../interfaces/user.interface';
import { LoginRequest } from '../interfaces/login.interface';
import { ProfileEnum } from '../enums/role.enum';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly TOKEN_KEY = 'jwtToken';
    private readonly ROLE_KEY = 'roles';
    private readonly CURRENT_USER_KEY = 'currentUser';
    private readonly API_URL = environment.apiUrls.authApi;

    private isAuthenticated$ = new BehaviorSubject<boolean>(false);
    private currentUser$ = new BehaviorSubject<{ name: string, email: string } | null>(null);
    private readonly currentUserSubject: BehaviorSubject<any>;
    public currentUser: Observable<any>;

    constructor(private http: HttpClient, private router: Router) {
        const storedUser = JSON.parse(
            localStorage.getItem('currentUser') ||
            sessionStorage.getItem('currentUser') ||
            '{}'
        );

        this.currentUserSubject = new BehaviorSubject<any>(storedUser);
        this.currentUser = this.currentUserSubject.asObservable();

        if (this.getToken()) {
            this.currentUserSubject.next(storedUser);
        }
    }

    register(request: RegisterRequest) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        request.roles = ProfileEnum.getFullAccess();
        return this.http.post<any>(`${this.API_URL}/users/register`, request, { headers });
    }

    login(request: LoginRequest): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const body = { email: request.email, password: request.password };

        return this.http.post<any>(`${this.API_URL}/auth/login`, body, { headers })
            .pipe(
                tap((response: AuthResponse) => {
                    const decodedToken: any = jwtDecode(response.accessToken);
                    this.save(response, request.rememberMe, decodedToken);
                })
            );
    }

    logout(): void {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        this.http.post<any>(`${this.API_URL}/auth/logout`, null, { headers })
            .pipe(
                tap(out => {
                    console.log('Logout response:', out);
                })
            )
            .subscribe({
                next: () => {
                    this.clear();
                    this.currentUserSubject.next(null);
                    this.router.navigate(['/public/login']);
                },
                error: (err) => {
                    console.error('Logout error:', err);
                    // Ainda assim limpa e redireciona em caso de erro
                    this.clear();
                    this.currentUserSubject.next(null);
                    this.router.navigate(['/public/login']);
                }
            });
    }

    resendConfirmation(token: string) {
        return this.http.get<any>(`${this.API_URL}/users/resend?token=${token}`);
    }

    getToken() {
        return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
    }

    getCurrentUserInfo() {
        return localStorage.getItem(this.CURRENT_USER_KEY) || sessionStorage.getItem(this.CURRENT_USER_KEY);
    }

    clearSessionAndRedirect(): void {
        this.clear();
        this.currentUserSubject.next(null);
        this.router.navigate(['/public/login'], {
            queryParams: { reason: 'session-expired' }
        });
    }

    getRoles(): string[] {
        const rolesString = localStorage.getItem(this.ROLE_KEY);
        try {
            return rolesString ? JSON.parse(rolesString) : [];
        } catch {
            return [];
        }
    }

    private clear() {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('roles');
        sessionStorage.removeItem(this.TOKEN_KEY);
        sessionStorage.removeItem('currentUser');
        sessionStorage.removeItem('roles');
    }

    private save(user: AuthResponse, rememberMe: boolean, decodedToken: any) {
        if (user.accessToken) {
            const userToSave = {
                ...user,
                user: {
                    ...user.user,
                    tenantId: parseInt(decodedToken.jti)
                }
            }

            localStorage.removeItem('countResended');
            localStorage.removeItem('resendCooldown');
            if (rememberMe) {
                localStorage.setItem('jwtToken', user.accessToken);
                localStorage.setItem('currentUser', JSON.stringify(userToSave));
                localStorage.setItem('roles', JSON.stringify(decodedToken.roles));
            } else {
                sessionStorage.setItem('jwtToken', user.accessToken);
                sessionStorage.setItem('currentUser', JSON.stringify(userToSave));
                localStorage.setItem('roles', JSON.stringify(decodedToken.roles));
            }
            this.currentUserSubject.next(userToSave);
        }
    }

}
